import { useLocation } from "react-router-dom";
import { Icons } from "@/components/ui/icons";
import { DenkerLogo } from "@/components/ui/logo";
import { useT } from "@/i18n";
import { layoutMessages } from "@/i18n/messages/layout";

/* Routes where the small-screen blocker is intentionally skipped. Auth pages
 * (sign-up, sign-in, password reset, email verify, OAuth callbacks) must remain
 * usable on a phone so visitors can save their spot before coming back on a
 * Mac. The canvas / workspace is what actually needs the desktop. */
const ALLOWED_PATH_PREFIXES = ["/auth/", "/oauth/"];

export function ScreenSizeGuard() {
  const t = useT(layoutMessages);
  const { pathname } = useLocation();
  if (ALLOWED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return null;
  }

  return (
    <div
      className="screen-size-blocker fixed inset-0 z-[9999] hidden flex-col items-center justify-center overflow-hidden bg-canvas"
      data-testid="screen-size-blocker"
      style={{
        backgroundImage:
          "radial-gradient(circle, var(--color-canvas-dot) 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }}
    >
      {/* Page gradient overlay — same as auth layout */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{ background: "var(--gradient-page-overlay)" }}
      />

      {/* Glass card */}
      <div className="relative z-10 flex flex-col items-center gap-6 rounded-panel border border-glass-stroke-light bg-glass-fill p-10 shadow-glass backdrop-blur-glass">
        {/* Specular top-edge highlight */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-panel"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.60) 50%, transparent 100%)",
          }}
          aria-hidden="true"
        />

        <DenkerLogo variant="wordmark" color="auto" height={28} />

        <Icons.Monitor className="h-10 w-10 text-muted" />

        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-appkit-title font-semibold text-primary">
            {t("desktopNeededTitle")}
          </h1>
          <p className="max-w-xs text-appkit-subhead text-secondary">
            {t("desktopNeededBody")}
          </p>
        </div>

        <p
          className="max-w-xs text-center text-appkit-small text-muted"
          data-testid="screen-size-blocker-coming-soon"
        >
          {t("mobileComingSoon")}
        </p>
      </div>
    </div>
  );
}
